import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

const EXPANDED_CLASS = "com-searchDispoContainerB2C--expanded";
const STICKY_EXPANDED_CLASS =
  "fly-availability__searchDispo-container--toggle-expanded";
const MOBILE_MQ = "(max-width: 767px)";
const FADE_DURATION_MS = 350;

/**
 * Alterna entre `ComSearchB2CNonEditable` y `FlySearchB2CDispo` en dispo de vuelos.
 * Desktop: fundido in-place. Mobile: abre modal con el buscador editable.
 */
export class FlySearchDispoToggle implements Plugin {
  private trigger: HTMLButtonElement | null = null;
  private closeButton: HTMLButtonElement | null = null;
  private nonEditableView: HTMLElement | null = null;
  private editableView: HTMLElement | null = null;
  private modal: HTMLElement | null = null;
  private stickyContainer: HTMLElement | null = null;
  private availabilityRoot: HTMLElement | null = null;
  private static readonly TOGGLE_EXPAND_TOP_VAR =
    "--fly-summary-toggle-expand-top";
  private modalClosedHandler: (() => void) | null = null;
  private isExpanded = false;
  private mobileMq: MediaQueryList | null = null;
  private clickHandler: ((event: Event) => void) | null = null;
  private closeButtonHandler: (() => void) | null = null;
  private keydownHandler: ((event: KeyboardEvent) => void) | null = null;
  private mqHandler: ((event: MediaQueryListEvent) => void) | null = null;
  private collapseTimer: number | null = null;
  private readonly bodyOpenClass = "fly-dispo-search-modal-open";

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    this.trigger = this.element.querySelector(
      ".com-searchB2CNonEditable .com-searchButtonB2C__button",
    );
    this.nonEditableView = this.element.querySelector(
      '[data-search-view="non-editable"]',
    );
    this.editableView = this.element.querySelector(
      '[data-search-view="editable"]',
    );
    this.stickyContainer = this.element.closest(
      ".fly-availability__searchDispo-container",
    ) as HTMLElement | null;
    this.availabilityRoot = this.element.closest(
      ".fly-availability",
    ) as HTMLElement | null;

    const modalId = this.element.dataset.searchModalId;
    this.modal = modalId ? document.getElementById(modalId) : null;
    this.portalModalToBody();

    if (!this.trigger || !this.editableView) return;

    this.modalClosedHandler = () => {
      document.documentElement.classList.remove(this.bodyOpenClass);
    };
    this.modal?.addEventListener("modal:closed", this.modalClosedHandler);

    this.clickHandler = (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (this.isMobile()) {
        this.openMobileModal();
        return;
      }

      if (this.isExpanded) {
        this.collapseDesktop();
      } else {
        this.expandDesktop();
      }
    };

    this.trigger.addEventListener("click", this.clickHandler);

    this.closeButton = this.editableView.querySelector<HTMLButtonElement>(
      ".fly-searchB2CFlightType__close",
    );
    if (this.closeButton) {
      this.closeButtonHandler = () => {
        if (!this.isMobile()) this.collapseDesktop();
      };
      this.closeButton.addEventListener("click", this.closeButtonHandler);
    }

    this.keydownHandler = (event) => {
      if (event.key === "Escape" && this.isExpanded && !this.isMobile()) {
        this.collapseDesktop();
      }
    };
    document.addEventListener("keydown", this.keydownHandler);

    if (typeof window.matchMedia === "function") {
      this.mobileMq = window.matchMedia(MOBILE_MQ);
      this.mqHandler = (event) => {
        if (event.matches && this.isExpanded) {
          this.collapseDesktop(false);
        }
      };
      this.mobileMq.addEventListener("change", this.mqHandler);
    }
  }

  destroy(): void {
    this.clearCollapseTimer();
    document.documentElement.classList.remove(this.bodyOpenClass);

    if (this.modal && this.modalClosedHandler) {
      this.modal.removeEventListener("modal:closed", this.modalClosedHandler);
    }

    if (this.trigger && this.clickHandler) {
      this.trigger.removeEventListener("click", this.clickHandler);
    }

    if (this.closeButton && this.closeButtonHandler) {
      this.closeButton.removeEventListener("click", this.closeButtonHandler);
    }

    if (this.keydownHandler) {
      document.removeEventListener("keydown", this.keydownHandler);
    }

    if (this.mobileMq && this.mqHandler) {
      this.mobileMq.removeEventListener("change", this.mqHandler);
    }
  }

  private isMobile(): boolean {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  /**
   * La modal vive en el slot de búsqueda, dentro de un contenedor sticky con
   * clip-path/will-change que rompe `position: fixed`. La movemos a `body`.
   */
  private portalModalToBody(): void {
    if (!this.modal || this.modal.dataset.portaled === "true") return;

    document.body.appendChild(this.modal);
    this.modal.dataset.portaled = "true";
  }

  private openMobileModal(): void {
    document.documentElement.classList.add(this.bodyOpenClass);
    this.modal?.dispatchEvent(new CustomEvent("modal:open"));
  }

  private expandDesktop(): void {
    if (this.isExpanded || !this.nonEditableView || !this.editableView) return;

    this.isExpanded = true;
    this.editableView.removeAttribute("hidden");
    this.element.classList.add(EXPANDED_CLASS);
    this.stickyContainer?.classList.add(STICKY_EXPANDED_CLASS);

    // Limpiar inmediatamente los estados sticky del journey (--stuck / --stopped
    // + inline styles de mobile-fixed). El plugin flyAvailabilityLayout los
    // mantiene limpios en cada scroll posterior mientras toggle-expanded esté activo,
    // pero sin esta llamada inicial quedarían del estado previo al clic.
    this.clearJourneyStuckStates();

    // Medir la altura real del contenedor expandido y empujar el summary bar
    requestAnimationFrame(() => {
      if (!this.isExpanded || !this.stickyContainer || !this.availabilityRoot)
        return;
      const expandedH = this.stickyContainer.scrollHeight;
      if (expandedH > 0) {
        this.availabilityRoot.style.setProperty(
          FlySearchDispoToggle.TOGGLE_EXPAND_TOP_VAR,
          `${expandedH}px`,
        );
      }
      if (!this.isStickyFixed()) {
        this.syncLayoutAfterTransition();
      }
    });
  }

  /** True si el buscador está en posición fixed (sticky revelado o pinned mobile). */
  private isStickyFixed(): boolean {
    return !!(
      this.stickyContainer?.classList.contains(
        "fly-availability__searchDispo-container--summary-revealed",
      ) ||
      this.stickyContainer?.classList.contains(
        "fly-availability__searchDispo-container--mobile-pinned",
      )
    );
  }

  /**
   * True si la disponibilidad está en modo summary-search (buscador compacto
   * que se revela por scroll en desktop). En este modo el layout es gestionado
   * exclusivamente por los scroll-events de flyAvailabilityLayout, por lo que
   * no es necesario —ni conveniente— disparar un evento resize artificial.
   */
  private isSummarySearchMode(): boolean {
    return (
      this.availabilityRoot?.classList.contains(
        "fly-availability--summary-search-mode",
      ) ?? false
    );
  }

  private collapseDesktop(shouldSyncLayout = true): void {
    if (!this.isExpanded) return;

    this.isExpanded = false;
    this.element.classList.remove(EXPANDED_CLASS);
    this.stickyContainer?.classList.remove(STICKY_EXPANDED_CLASS);
    this.availabilityRoot?.style.removeProperty(
      FlySearchDispoToggle.TOGGLE_EXPAND_TOP_VAR,
    );

    this.clearCollapseTimer();
    this.collapseTimer = window.setTimeout(() => {
      this.editableView?.setAttribute("hidden", "");
      this.collapseTimer = null;
      if (shouldSyncLayout && !this.isStickyFixed()) {
        this.syncLayoutAfterTransition();
      }
    }, FADE_DURATION_MS);
  }

  private clearCollapseTimer(): void {
    if (this.collapseTimer !== null) {
      window.clearTimeout(this.collapseTimer);
      this.collapseTimer = null;
    }
  }

  private clearJourneyStuckStates(): void {
    if (!this.availabilityRoot) return;
    const selector =
      ".fly-rtDefaultB2C > .fly-stickyInfoB2C, .fly-rtFeaturedB2C > .fly-stickyInfoB2C, .fly-owDefaultB2C > .fly-stickyInfoB2C";
    this.availabilityRoot
      .querySelectorAll<HTMLElement>(selector)
      .forEach((el) => {
        el.classList.remove(
          "fly-stickyInfoB2C--stuck",
          "fly-stickyInfoB2C--stopped",
        );
        el.style.removeProperty("--fly-sticky-stop-top");
        el.style.removeProperty("position");
        el.style.removeProperty("top");
        el.style.removeProperty("left");
        el.style.removeProperty("width");
        el.style.removeProperty("right");
      });
  }

  private syncLayoutAfterTransition(): void {
    window.setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, FADE_DURATION_MS);
  }
}

PluginRegistry.register("flySearchDispoToggle", FlySearchDispoToggle);
